/**
 * /practice-sale/:token — the one form a member fills in when they sell their
 * practice (design docs/plans/2026-11-04-practice-sale-licence-transfer-design.md
 * section 3; build job mct_e446474b-67c, PR 1). The seller holds the only link;
 * the token is the whole authority, and nothing about them rides in the URL.
 *
 * It asks about the buyer, the sale, the team and whether the seller is staying on,
 * and ends with the release, kept word for word. It never asks for a card or any
 * payment detail. What the buyer pays is worked out by the Engine afterwards.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND_NAME } from './branding';
import { useNoIndex } from './lib/useNoIndex';

const API = import.meta.env.VITE_API_BASE_URL as string;
const TSB_LOGO = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-light.svg';

interface PageData {
  ok: boolean;
  state: 'open' | 'received' | 'closed';
  seller_first_name: string | null;
  firm_name: string | null;
  team: string[];
  countries: Array<{ code: string; label: string }>;
}

export function PracticeSalePage() {
  const { token = '' } = useParams<{ token: string }>();
  useNoIndex(true);
  const [data, setData] = useState<PageData | null>(null);
  const [problem, setProblem] = useState<'link' | 'network' | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState({
    buyer_first_name: '', buyer_last_name: '', buyer_email: '', buyer_phone: '', trading_name: '', buyer_country: '',
    handover_date: '', workspace_to_buyer: true, seller_staying: '', release: false,
  });
  const [team, setTeam] = useState<Record<string, boolean | null>>({});

  useEffect(() => {
    document.title = `Selling your practice · ${BRAND_NAME}`;
    return () => { document.title = BRAND_NAME; };
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/practice-sale/${encodeURIComponent(token)}`);
        if (!live) return;
        if (!r.ok) { setProblem('link'); return; }
        const d = await r.json() as PageData;
        setData(d);
        setTeam(Object.fromEntries(d.team.map((e) => [e, null])));
      } catch {
        if (live) setProblem('network');
      }
    })();
    return () => { live = false; };
  }, [token]);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value });
  const buyerName = `${f.buyer_first_name} ${f.buyer_last_name}`.trim() || 'the buyer';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/public/practice-sale/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, team: Object.entries(team).map(([email, staying]) => ({ email, staying })) }),
      });
      const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
      if (r.ok && d.ok) setSent(true);
      else setError(d.error || 'Something went wrong. Please try again, or reply to our email and we will help.');
    } catch {
      setError('We could not reach our servers. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={wrap}>
      <header style={nav}>
        <div style={navInner}>
          <a href="https://www.thesuccessfulbookkeeper.com"><img src={TSB_LOGO} alt="The Successful Bookkeeper" style={{ height: 32 }} /></a>
        </div>
      </header>
      <main style={main}>
        <p style={eyebrow}>Selling your practice</p>
        <h1 style={h1}>Hi{data?.seller_first_name ? ` ${data.seller_first_name}` : ''}, tell us about the sale</h1>
        {problem === 'link' && <section style={card}><p style={p0}>This link has expired or is not complete. Reply to the email that brought you here and we'll send you a fresh one.</p></section>}
        {problem === 'network' && <section style={card}><p style={p0}>We could not reach our servers. Please check your connection and reload this page.</p></section>}
        {!problem && !data && <section style={card}><p style={{ ...p0, opacity: 0.75 }}>One moment…</p></section>}
        {data && data.state === 'closed' && <section style={card}><p style={p0}>This sale has been closed on our side. If that's not right, reply to our email and we'll sort it out with you.</p></section>}
        {data && (data.state === 'received' || sent) && (
          <section style={card}>
            <p style={p0}><b>Thank you, we have everything we need.</b></p>
            <p style={{ ...p0, marginTop: 10 }}>
              Our client success team will check the details about {sent ? buyerName : 'the buyer'} and the handover. Then we'll email you your buyer's
              personal links to pass on: the call with Debbie or Ron, and the options from our email. Your billing stays as it is until the handover.
            </p>
          </section>
        )}
        {data && data.state === 'open' && !sent && (
          <form onSubmit={submit}>
            <p style={lede}>
              Congratulations on the sale{data.firm_name ? ` of ${data.firm_name}` : ''}. This takes about five minutes. It's the first step from our
              email: tell us about your buyer and the handover, so we can pass your licence on with the options we offered and keep your team
              connected. We never ask for anyone's card details here.
            </p>

            <section style={card}>
              <h2 style={h2}>About the buyer</h2>
              <div style={row}>
                <label style={lab}>First name<input style={inp} value={f.buyer_first_name} onChange={set('buyer_first_name')} required /></label>
                <label style={lab}>Last name<input style={inp} value={f.buyer_last_name} onChange={set('buyer_last_name')} required /></label>
              </div>
              <div style={row}>
                <label style={lab}>Email<input style={inp} type="email" value={f.buyer_email} onChange={set('buyer_email')} required /></label>
                <label style={lab}>Phone<input style={inp} type="tel" value={f.buyer_phone} onChange={set('buyer_phone')} required /></label>
              </div>
              <label style={lab}>Practice name they'll trade under (only if they're renaming it)<input style={inp} value={f.trading_name} onChange={set('trading_name')} /></label>
              <label style={lab}>The country they're in
                <select style={inp} value={f.buyer_country} onChange={set('buyer_country')} required>
                  <option value="">Choose…</option>
                  {data.countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </label>
            </section>

            <section style={card}>
              <h2 style={h2}>About the sale</h2>
              <label style={lab}>Handover date<input style={inp} type="date" value={f.handover_date} onChange={set('handover_date')} required /></label>
              <p style={{ ...p0, marginTop: 12 }}>Your BKOS workspace and its client data:</p>
              <label style={radio}><input type="radio" checked={f.workspace_to_buyer} onChange={() => setF({ ...f, workspace_to_buyer: true })} /> Go to the buyer (it holds the practice's clients)</label>
              <label style={radio}><input type="radio" checked={!f.workspace_to_buyer} onChange={() => setF({ ...f, workspace_to_buyer: false })} /> Don't go to the buyer (export what you need before the handover)</label>
            </section>

            {data.team.length > 0 && (
              <section style={card}>
                <h2 style={h2}>Your team</h2>
                <p style={p0}>For each person connected to your practice, tell us whether they're staying with the practice or leaving.</p>
                {data.team.map((email) => (
                  <div key={email} style={{ marginTop: 12 }}>
                    <div style={{ fontWeight: 600 }}>{email}</div>
                    <label style={radio}><input type="radio" name={`t-${email}`} checked={team[email] === true} onChange={() => setTeam({ ...team, [email]: true })} required /> Staying with the practice</label>
                    <label style={radio}><input type="radio" name={`t-${email}`} checked={team[email] === false} onChange={() => setTeam({ ...team, [email]: false })} /> Leaving</label>
                  </div>
                ))}
              </section>
            )}

            <section style={card}>
              <h2 style={h2}>You</h2>
              <p style={p0}>Are you staying on with the practice?</p>
              <label style={radio}><input type="radio" name="staying" checked={f.seller_staying === 'no'} onChange={() => setF({ ...f, seller_staying: 'no' })} required /> No</label>
              <label style={radio}><input type="radio" name="staying" checked={f.seller_staying === 'team'} onChange={() => setF({ ...f, seller_staying: 'team' })} /> Yes, as Team</label>
              <label style={radio}><input type="radio" name="staying" checked={f.seller_staying === 'manager'} onChange={() => setF({ ...f, seller_staying: 'manager' })} /> Yes, as Manager</label>
            </section>

            <section style={card}>
              <h2 style={h2}>The release</h2>
              <label style={{ ...radio, alignItems: 'flex-start' }}>
                <input type="checkbox" checked={f.release} onChange={set('release')} required style={{ marginTop: 4 }} />
                <span>I release my licence to {buyerName} from {f.handover_date || 'the handover date'}. Please stop my billing then.</span>
              </label>
            </section>

            {error && <p style={{ color: '#b42318', margin: '0 0 12px' }}>{error}</p>}
            <button type="submit" style={btn} disabled={busy}>{busy ? 'Sending…' : 'Send the details'}</button>
            <p style={fine}>Questions? Reply to the email that brought you here and a real person on our team will help.</p>
          </form>
        )}
      </main>
    </div>
  );
}

const INK = '#0A0A2E';
const wrap: React.CSSProperties = { minHeight: '100vh', background: '#F8F6F3', color: INK, fontFamily: 'var(--font-body, "Poppins", -apple-system, sans-serif)' };
const nav: React.CSSProperties = { padding: '12px 24px', borderBottom: '1px solid rgba(10,10,46,0.08)', background: '#fff' };
const navInner: React.CSSProperties = { maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center' };
const main: React.CSSProperties = { maxWidth: 760, margin: '0 auto', padding: '40px 16px 72px' };
const eyebrow: React.CSSProperties = { textTransform: 'uppercase', letterSpacing: 1.2, fontSize: 12, fontWeight: 600, opacity: 0.6, margin: '0 0 10px' };
const h1: React.CSSProperties = { fontFamily: 'var(--font-heading, "Fraunces", Georgia, serif)', fontSize: 'clamp(28px, 4.2vw, 38px)', fontWeight: 600, lineHeight: 1.15, margin: '0 0 16px' };
const h2: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: '0 0 12px' };
const lede: React.CSSProperties = { fontSize: 17, lineHeight: 1.6, opacity: 0.85, margin: '0 0 24px' };
const card: React.CSSProperties = { background: '#fff', borderRadius: 10, padding: '22px 24px', marginBottom: 18, borderLeft: '4px solid var(--prog-primary, #FF6B35)', lineHeight: 1.55 };
const p0: React.CSSProperties = { margin: 0 };
const row: React.CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, fontWeight: 500, marginBottom: 12, flex: '1 1 220px' };
const inp: React.CSSProperties = { fontSize: 15, padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontFamily: 'inherit', background: '#fff' };
const radio: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', fontSize: 15, marginTop: 6 };
const btn: React.CSSProperties = { fontSize: 16, fontWeight: 600, padding: '12px 22px', borderRadius: 8, border: 'none', background: 'var(--prog-primary, #FF6B35)', color: '#fff', cursor: 'pointer' };
const fine: React.CSSProperties = { fontSize: 13, opacity: 0.7, margin: '14px 0 0', lineHeight: 1.5 };

export default PracticeSalePage;
